import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { useTenant } from '../contexts/TenantContext';
import { useT } from '../i18n';

/**
 * Análise dos clientes a partir das conversas: em que fase está cada um (fiel, ativo, novo, em risco,
 * uma vez só, parado) e de onde veio (anúncio, post, link, campanha nossa, nós iniciamos, ele iniciou).
 */

type Segment = 'LOYAL' | 'ACTIVE' | 'NEW' | 'AT_RISK' | 'ONE_TIME' | 'DORMANT';
type Origin = 'AD' | 'POST' | 'LINK' | 'CAMPAIGN' | 'OUTREACH' | 'DIRECT';

interface Customer {
  phone: string;
  name: string | null;
  segment: Segment;
  origin: Origin;
  ad: { id: string | null; title: string | null; url: string | null } | null;
  firstMessageAt: string;
  lastInboundAt: string;
  inboundMessages: number;
  activeDays: number;
  activeDays90: number;
  numbers: string[];
}

interface Report {
  generatedAt: string;
  dataSince: string | null;
  rules: Record<string, number>;
  total: number;
  segments: Record<Segment, number>;
  origins: Record<Origin, number>;
  matrix: Record<Segment, Record<Origin, number>>;
  ads: { id: string | null; title: string | null; url: string | null; customers: number; loyal: number; active: number }[];
  customers: Customer[];
}

const SEGMENTS: Segment[] = ['LOYAL', 'ACTIVE', 'NEW', 'AT_RISK', 'ONE_TIME', 'DORMANT'];
const ORIGINS: Origin[] = ['AD', 'POST', 'LINK', 'CAMPAIGN', 'OUTREACH', 'DIRECT'];
const PAGE = 50;

const SEGMENT_STYLE: Record<Segment, string> = {
  LOYAL: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  ACTIVE: 'border-blue-300 bg-blue-50 text-blue-900',
  NEW: 'border-violet-300 bg-violet-50 text-violet-900',
  AT_RISK: 'border-amber-300 bg-amber-50 text-amber-900',
  ONE_TIME: 'border-gray-300 bg-gray-50 text-gray-800',
  DORMANT: 'border-slate-300 bg-slate-100 text-slate-700',
};

export function CustomerInsightsPage() {
  const t = useT();
  const { selectedTenantId } = useTenant();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState<Segment | null>(null);
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` };
      if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
      const response = await fetch('/api/customer-insights', { headers });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
      setReport(data);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [segment, origin, search]);

  const filtered = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return report.customers.filter(c =>
      (!segment || c.segment === segment)
      && (!origin || c.origin === origin)
      && (!q || c.phone.includes(q.replace(/\D/g, '') || q) || (c.name || '').toLowerCase().includes(q) || (c.ad?.title || '').toLowerCase().includes(q)));
  }, [report, segment, origin, search]);

  const pct = (n: number) => (report && report.total ? `${Math.round((n * 100) / report.total)}%` : '0%');
  const date = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const rules = report?.rules || {};

  const exportCsv = () => {
    const header = ['name', 'phone', 'segment', 'origin', 'ad', 'first_message', 'last_message', 'active_days', 'active_days_90', 'messages', 'numbers'];
    const quote = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = filtered.map(c => [
      c.name, `+${c.phone}`, t(`ci.segment.${c.segment}`), t(`ci.origin.${c.origin}`), c.ad?.title || c.ad?.id || '',
      c.firstMessageAt.slice(0, 10), c.lastInboundAt.slice(0, 10), c.activeDays, c.activeDays90, c.inboundMessages, c.numbers.join(' | '),
    ].map(quote).join(','));
    // BOM: o Excel abre o árabe certo
    const blob = new Blob(['﻿' + [header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <Header title={t('ci.title')} subtitle={t('ci.subtitle')}
        actions={
          <div className="flex gap-2">
            <button onClick={load} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
              {loading ? t('common.loading') : t('ci.refresh')}
            </button>
            <button onClick={exportCsv} disabled={!report || filtered.length === 0} className="px-4 py-2 bg-gray-100 rounded-lg text-sm disabled:opacity-50">
              {t('ci.export')}
            </button>
          </div>
        } />

      <div className="p-6 space-y-6">
        {!report && loading && <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">{t('common.loading')}</div>}

        {report && (
          <>
            <p className="text-sm text-gray-600 bg-white rounded-lg shadow px-4 py-3">
              {report.total === 0 ? t('ci.empty') : t('ci.dataNote', { total: report.total, since: report.dataSince ? date(report.dataSince) : '—' })}
            </p>

            {/* Fases */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('ci.segmentsTitle')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {SEGMENTS.map(s => (
                  <button key={s} type="button" onClick={() => setSegment(segment === s ? null : s)}
                    className={`text-start rounded-lg border p-4 transition-shadow ${SEGMENT_STYLE[s]} ${segment === s ? 'ring-2 ring-offset-1 ring-blue-500' : 'hover:shadow'}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold">{t(`ci.segment.${s}`)}</span>
                      <span className="text-2xl font-bold tabular-nums">{report.segments[s]}</span>
                    </div>
                    <div className="text-xs mt-1 opacity-80">{pct(report.segments[s])} · {t(`ci.segmentRule.${s}`, rules)}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Origem */}
            <section className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('ci.originsTitle')}</h2>
                <p className="text-xs text-gray-500 mb-3">{t('ci.originsHelp')}</p>
                <table className="w-full text-sm">
                  <tbody className="divide-y">
                    {ORIGINS.map(o => (
                      <tr key={o} onClick={() => setOrigin(origin === o ? null : o)}
                        className={`cursor-pointer ${origin === o ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                        <td className="py-2 pe-2">
                          <div className="font-medium text-gray-800">{t(`ci.origin.${o}`)}</div>
                          <div className="text-xs text-gray-500">{t(`ci.originHelp.${o}`)}</div>
                        </td>
                        <td className="py-2 text-end tabular-nums font-semibold">{report.origins[o]}</td>
                        <td className="py-2 ps-2 text-end tabular-nums text-gray-500 w-14">{pct(report.origins[o])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white rounded-lg shadow p-4 overflow-x-auto">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('ci.matrixTitle')}</h2>
                <p className="text-xs text-gray-500 mb-3">{t('ci.matrixHelp')}</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500">
                      <th className="text-start py-1 pe-2"></th>
                      {ORIGINS.map(o => <th key={o} className="py-1 px-1 text-end font-medium">{t(`ci.originShort.${o}`)}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {SEGMENTS.map(s => (
                      <tr key={s}>
                        <td className="py-2 pe-2 font-medium text-gray-800 whitespace-nowrap">{t(`ci.segment.${s}`)}</td>
                        {ORIGINS.map(o => {
                          const n = report.matrix[s][o];
                          return (
                            <td key={o} className="py-2 px-1 text-end tabular-nums">
                              {n > 0
                                ? <button className="hover:underline" onClick={() => { setSegment(s); setOrigin(o); }}>{n}</button>
                                : <span className="text-gray-300">0</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Anúncios */}
            <section className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('ci.adsTitle')}</h2>
              {report.ads.length === 0 ? (
                <p className="text-sm text-gray-500">{t('ci.adsEmpty')}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500">
                      <th className="text-start py-1">{t('ci.ad')}</th>
                      <th className="text-end py-1">{t('ci.adCustomers')}</th>
                      <th className="text-end py-1">{t('ci.adActive')}</th>
                      <th className="text-end py-1">{t('ci.adLoyal')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {report.ads.map((ad, i) => (
                      <tr key={`${ad.id}-${i}`}>
                        <td className="py-2 pe-2">
                          <div className="font-medium text-gray-800" dir="auto">{ad.title || t('ci.adUntitled')}</div>
                          <div className="text-xs text-gray-500" dir="ltr">
                            {ad.id && <>ID {ad.id} </>}
                            {ad.url && <a href={ad.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{t('ci.adOpen')}</a>}
                          </div>
                        </td>
                        <td className="py-2 text-end tabular-nums font-semibold">{ad.customers}</td>
                        <td className="py-2 text-end tabular-nums">{ad.active}</td>
                        <td className="py-2 text-end tabular-nums">{ad.loyal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="text-xs text-gray-500 mt-2">{t('ci.adsNote')}</p>
            </section>

            {/* Lista */}
            <section className="bg-white rounded-lg shadow">
              <div className="p-4 flex flex-wrap items-center gap-2 border-b">
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('ci.search')}
                  className="flex-1 min-w-[12rem] px-3 py-2 border border-gray-300 rounded-md text-sm" />
                {segment && <button onClick={() => setSegment(null)} className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{t(`ci.segment.${segment}`)} ×</button>}
                {origin && <button onClick={() => setOrigin(null)} className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{t(`ci.origin.${origin}`)} ×</button>}
                <span className="text-sm text-gray-500">{t('ci.showing', { n: filtered.length })}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="text-start p-2">{t('ci.col.customer')}</th>
                      <th className="text-start p-2">{t('ci.col.segment')}</th>
                      <th className="text-start p-2">{t('ci.col.origin')}</th>
                      <th className="text-start p-2">{t('ci.col.first')}</th>
                      <th className="text-start p-2">{t('ci.col.last')}</th>
                      <th className="text-end p-2">{t('ci.col.days')}</th>
                      <th className="text-end p-2">{t('ci.col.messages')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filtered.slice(page * PAGE, (page + 1) * PAGE).map(c => (
                      <tr key={c.phone} className="align-top">
                        <td className="p-2">
                          <div className="font-medium text-gray-900" dir="auto">{c.name || '—'}</div>
                          <div className="text-xs text-gray-500" dir="ltr">+{c.phone}</div>
                          <div className="text-xs text-gray-400">{c.numbers.join(' · ')}</div>
                        </td>
                        <td className="p-2"><span className={`px-2 py-0.5 rounded-full border text-xs ${SEGMENT_STYLE[c.segment]}`}>{t(`ci.segment.${c.segment}`)}</span></td>
                        <td className="p-2">
                          <div>{t(`ci.origin.${c.origin}`)}</div>
                          {c.ad?.title && <div className="text-xs text-gray-500" dir="auto">{c.ad.title}</div>}
                        </td>
                        <td className="p-2 whitespace-nowrap">{date(c.firstMessageAt)}</td>
                        <td className="p-2 whitespace-nowrap">{date(c.lastInboundAt)}</td>
                        <td className="p-2 text-end tabular-nums" title={t('ci.daysTitle')}>{c.activeDays} <span className="text-gray-400">({c.activeDays90})</span></td>
                        <td className="p-2 text-end tabular-nums">{c.inboundMessages}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filtered.length > PAGE && (
                <div className="p-3 flex items-center justify-between text-sm border-t">
                  <button disabled={page === 0} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded border disabled:opacity-40">{t('ci.prev')}</button>
                  <span className="text-gray-500">{page + 1} / {Math.ceil(filtered.length / PAGE)}</span>
                  <button disabled={(page + 1) * PAGE >= filtered.length} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded border disabled:opacity-40">{t('ci.next')}</button>
                </div>
              )}
              <p className="text-xs text-gray-500 p-3 border-t">{t('ci.daysNote')}</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
